import React, { Fragment, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor, State } from '@progress/kendo-data-query'
import { Filter, FilterChangeEvent } from '@progress/kendo-react-data-tools'
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { IntroducingBrokerDetailsDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDetailsDto'
import { TradingAccountClientDto } from 'api/Trading-Accounts/Types/TradingAccountClientDto'
import { FlexBox } from 'components/Box'
import { getIntroducingBrokerClientsField } from 'components/Filter/introducingBrokerClientsField'
import { Spinner } from 'components/Spinner'
import { normalizeFilter } from 'features/DataGrid'
import { Search } from 'lucide-react'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'

import introducingBrokerClientDetailsColumn from './introducingBrokerClientDetailsColumn'
import {
  IntroducingBrokerNavigation,
  IntroducingBrokerTab,
} from './Navigation/IntroducingBrokerNavigation'

interface Values {
  paginationData: PageData<TradingAccountClientDto[]>
  id: string
}

type MyProps = Values

const createDataState = (
  state: State,
  tradingAccountClientDtos: TradingAccountClientDto[],
  total: number
) => ({
  result: tradingAccountClientDtos,
  dataState: state,
  total,
})

const IntroducingBrokerClientDetailsTable = ({ paginationData, id }: MyProps) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [loading, setLoading] = useState<boolean>(false)
  const [dataState, setDataState] = useState<{
    result: TradingAccountClientDto[]
    dataState: State
    total: number
  }>(
    createDataState(getPaginationInitialState(), paginationData.items, paginationData.totalResults)
  )
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'or', filters: [] })

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    setDataState(
      createDataState(
        event.dataState,
        paginationData.items as TradingAccountClientDto[],
        paginationData.totalResults
      )
    )
  }
  const onFilterChange = (event: FilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
  }
  const getQuery = () => buildQuery(filter)

  const pageChange = (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })
    const sessionPageSize = sessionStorage.getItem('itemsPerPage')
    if (event.page.take.toString() !== sessionPageSize)
      sessionStorage.setItem('itemsPerPage', event.page.take.toString())
    const query = getQuery()
    setLoading(true)
    if (!event.nativeEvent) {
      return partnershipsApiClient
        .getClientsForIntroducingBroker(id, query, 1, take)
        .then((data) => {
          setDataState(
            createDataState(
              {
                take: pagination.getRecordsPerPage(),
                skip,
              },
              data.items,
              data.totalResults
            )
          )
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }
    if (pagination.getPageIndex() > 0) {
      partnershipsApiClient
        .getClientsForIntroducingBroker(
          id,
          query,
          pagination.getPageIndex(),
          pagination.getRecordsPerPage()
        )
        .then((data) => {
          setDataState(
            createDataState(
              {
                take: pagination.getRecordsPerPage(),
                skip,
              },
              data.items as TradingAccountClientDto[],
              data.totalResults
            )
          )
          setLoading(false)
        })
        .catch(() => setLoading(false))
    }
  }

  const onFilterFetch = () => {
    const query = getQuery()
    setLoading(true)
    partnershipsApiClient
      .getClientsForIntroducingBroker(id, query)
      .then((data) => {
        setDataState(
          createDataState(
            dataState.dataState,
            data.items as TradingAccountClientDto[],
            data.totalResults
          )
        )
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
      })
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <>
      <FlexBox alignItems='center' justifyContent='space-between'>
        <Filter
          style={{ zIndex: 0 }}
          value={filter}
          onChange={onFilterChange}
          fields={getIntroducingBrokerClientsField()}
        />
        <Button type='button' onClick={onFilterFetch} data-test='filter-search-button'>
          <Search />
          Filter Search
        </Button>
      </FlexBox>
      <TableWithExport data={dataState.result || []}>
        <Grid
          resizable
          take={dataState.dataState.take}
          skip={dataState.dataState.skip}
          onDataStateChange={dataStateChange}
          total={dataState.total}
          data={dataState.result}
          style={{ zIndex: 0 }}
          sortable
          onPageChange={pageChange}
          pageable={pageableSettings}
        >
          {introducingBrokerClientDetailsColumn.map((column, index) => (
            <Column
              key={index}
              field={column.field}
              title={column.title}
              filter={column.filter}
              cell={column.cell}
              width={column.width}
              children={column.children}
              className={column.className}
            />
          ))}
        </Grid>
      </TableWithExport>
    </>
  )
}
interface Props {
  data: IntroducingBrokerDetailsDto
}
const IntroducingBrokerClientDetails: React.FC<Props> = ({ data }) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [loading, setLoading] = useState<boolean>(false)
  const [paginationData, setPaginationData] = useState<PageData<TradingAccountClientDto[]> | null>(
    null
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const response = await partnershipsApiClient.getClientsForIntroducingBroker(data.id, null)
        setPaginationData(response)
        return setLoading(false)
      } catch (error) {
        console.error(error)
        return setLoading(false)
      }
    }
    fetchData()
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <Spinner />
  }
  if (!paginationData) {
    return null
  }

  return (
    <Fragment>
      <IntroducingBrokerNavigation data={data} isEdit={false} tab={IntroducingBrokerTab.CLIENTS} />
      <IntroducingBrokerClientDetailsTable id={data.id} paginationData={paginationData} />
    </Fragment>
  )
}

export default IntroducingBrokerClientDetails
